import React, { useState } from 'react';
import { ActionableAdvice } from '../types';

interface CoachingTabHeaderProps {
  title: string;
  domain:
    | 'TIMING'
    | 'HARMONY'
    | 'TONE'
    | 'CLEANLINESS'
    | 'POSTURE'
    | 'NUANCE'
    | 'STABILITY'
    | 'ATTAQUE'
    | 'GENERAL';
  advices: ActionableAdvice[];
  score?: any;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
  auditInfo?: {
    measured: string | number;
    threshold: string | number;
    unit: string;
  };
}

export const CoachingTabHeader: React.FC<CoachingTabHeaderProps> = ({
  title,
  domain,
  advices,
  score,
  isFullscreen,
  onToggleFullscreen,
  auditInfo,
}) => {
  const [showAudit, setShowAudit] = useState(false);

  const m = auditInfo ? Number(auditInfo.measured) : 0;
  const t = auditInfo ? Number(auditInfo.threshold) : 1;

  // ✅ Domaines “plus bas = mieux” (erreurs, ms, etc.)
  // IMPORTANT: STABILITY est “plus haut = mieux” dans ton UX (%), donc PAS inverse.
  const isInverseDomain = domain === 'TIMING' || domain === 'CLEANLINESS';

  // ✅ Succès : inverse => m <= t, normal => m >= t
  const isSuccess = isInverseDomain ? m <= t : m >= t;

  // ✅ Rouge uniquement si dépassement réel (évite rouge quand m=0)
  const isOver = !!auditInfo && !isSuccess && m !== 0;

  // ✅ Jauge : doit représenter une “qualité” (0 erreur => 100%)
  let gaugePct = 0;

  if (!auditInfo) {
    gaugePct = 0;
  } else if (isInverseDomain) {
    // 0 => 100%, dépassement => vers 0%
    const denom = Math.max(1, t * 1.5);
    const ratio = Math.min(1, m / denom); // 0..1
    gaugePct = (1 - ratio) * 100; // 100..0
  } else {
    // Domaines en % (plus haut = mieux)
    gaugePct = Math.min(100, Math.max(0, m));
  }

  // Clamp visuel (évite barre invisible si audit affiché)
  if (auditInfo) gaugePct = Math.min(100, Math.max(5, gaugePct));

  return (
    <div className={`animate-fade-in ${isFullscreen ? 'absolute top-6 right-6 z-[100]' : 'mb-10'}`}>
      <div className={`flex flex-col md:flex-row justify-between items-center gap-8 ${isFullscreen ? '' : 'mb-8'}`}>
        {!isFullscreen && (
          <div className="text-center md:text-left">
            <h3 className="text-3xl md:text-5xl font-black text-white italic uppercase tracking-tighter leading-none mb-2">
              {title.split(' ')[0]} <span className="text-smart-accent">{title.split(' ')[1] || ''}</span>
            </h3>
            <p className="text-[10px] text-slate-500 font-black uppercase tracking-[0.4em] ml-2 italic">
              {domain} UNIT PROBES ACTIVE
            </p>
          </div>
        )}

        <div className="flex items-center gap-6">
          <button
            onClick={() => setShowAudit(!showAudit)}
            className={`px-6 py-3 rounded-2xl border-2 font-black uppercase text-[10px] tracking-widest transition-all ${
              showAudit ? 'bg-white text-black border-white' : 'bg-white/5 border-white/10 text-slate-500 hover:border-white'
            }`}
          >
            {showAudit ? 'HIDE DATA' : 'RAW DATA AUDIT'}
          </button>

          {onToggleFullscreen && (
            <button
              onClick={onToggleFullscreen}
              className="rounded-2xl w-16 h-16 bg-white/5 border-2 border-white/10 flex items-center justify-center text-xl hover:bg-white hover:text-black transition-all"
            >
              {isFullscreen ? '✕' : '⛶'}
            </button>
          )}
        </div>
      </div>

      {showAudit && !isFullscreen && (
        <div className="mb-10 p-10 bg-black/90 border-2 border-white/10 rounded-[56px] animate-fade-in-up shadow-3xl relative overflow-hidden backdrop-blur-3xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 relative z-10">
            <div className="space-y-8">
              <div className="flex items-center gap-4">
                <div className={`w-3 h-3 rounded-full ${isOver ? 'bg-red-500' : 'bg-emerald-500'}`}></div>
                <h4 className="text-[10px] font-black text-white uppercase tracking-[0.4em]">Algorithmic Threshold Trace</h4>
              </div>

              <div className="space-y-4">
                <div className="h-5 w-full bg-white/5 rounded-full p-1 border border-white/10">
                  <div
                    className={`h-full rounded-full transition-all duration-1000 ${isOver ? 'bg-red-600' : 'bg-emerald-500'}`}
                    style={{ width: `${gaugePct}%` }}
                  ></div>
                </div>

                <div className="flex justify-between text-[10px] font-black uppercase italic">
                  <span className="text-slate-500">Signal Probe</span>
                  <span className={isOver ? 'text-red-400' : 'text-emerald-400'}>
                    {isOver ? 'TOLERANCE_EXCEEDED' : 'NOMINAL_STATE'}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white/5 p-6 rounded-3xl border border-white/5 flex flex-col justify-center">
                <span className="text-[9px] font-black text-slate-500 uppercase mb-2">Measured</span>
                <span className="text-3xl font-black text-white italic tabular-nums">
                  {auditInfo?.measured ?? '—'}
                  <span className="text-xs ml-1 text-slate-600">{auditInfo?.unit}</span>
                </span>
              </div>

              <div className="bg-white/5 p-6 rounded-3xl border border-white/5 flex flex-col justify-center">
                <span className="text-[9px] font-black text-slate-500 uppercase mb-2">Threshold</span>
                <span className="text-3xl font-black text-slate-700 italic tabular-nums">
                  {auditInfo?.threshold ?? '—'}
                  <span className="text-xs ml-1 text-slate-800">{auditInfo?.unit}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
